import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Button,
  IconButton,
  Alert,
  Link,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Card,
  CardContent,
  Grid,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Article as ArticleIcon,
  Security as SecurityIcon,
  AccountBalance as LegalIcon,
  Update as UpdateIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  Share as ShareIcon
} from '@mui/icons-material';

 export const TermsAndConditions = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expanded, setExpanded] = useState('general');

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const lastUpdated = "September 17, 2025";
  const effectiveDate = "September 17, 2025";

  const termsData = [
    {
      id: 'general',
      title: 'General Terms',
      icon: <ArticleIcon />,
      content: [
        {
          subtitle: 'Agreement to Terms',
          text: 'By accessing and using the GlobalCommand Center platform ("Service"), you agree to be bound by these Terms and Conditions ("Terms"). If you disagree with any part of these terms, you may not access the Service.'
        },
        {
          subtitle: 'Platform Description',
          text: 'GlobalCommand Center is a comprehensive social media management platform that allows users to manage multiple social media accounts, schedule posts, analyze performance, and engage with audiences across various platforms.'
        },
        {
          subtitle: 'User Eligibility',
          text: 'You must be at least 18 years old or the age of majority in your jurisdiction to use our Service. By using the Service, you represent and warrant that you meet these age requirements.'
        }
      ]
    },
    {
      id: 'accounts',
      title: 'User Accounts & Registration',
      icon: <SecurityIcon />,
      content: [
        {
          subtitle: 'Account Creation',
          text: 'To access certain features of the Service, you must register for an account. You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate, current, and complete.'
        },
        {
          subtitle: 'Account Security',
          text: 'You are responsible for safeguarding the password and for all activities under your account. You agree to immediately notify us of any unauthorized use of your account or any other breach of security.'
        },
        {
          subtitle: 'Account Termination',
          text: 'We reserve the right to terminate or suspend your account immediately, without prior notice or liability, for any reason, including if you breach the Terms.'
        }
      ]
    },
    {
      id: 'usage',
      title: 'Acceptable Use Policy',
      icon: <WarningIcon />,
      content: [
        {
          subtitle: 'Permitted Use',
          text: 'You may use our Service only for lawful purposes and in accordance with these Terms. You agree not to use the Service in any way that could disable, overburden, damage, or impair the Service.'
        },
        {
          subtitle: 'Prohibited Activities',
          text: 'You agree not to: (a) use the Service for any unlawful purpose; (b) attempt to gain unauthorized access to other user accounts; (c) transmit viruses or malicious code; (d) spam or send unsolicited messages; (e) violate any applicable laws or regulations.'
        },
        {
          subtitle: 'Content Guidelines',
          text: 'You are solely responsible for the content you post through our Service. Content must not be illegal, harmful, threatening, abusive, defamatory, or violate any third-party rights.'
        }
      ]
    },
    {
      id: 'privacy',
      title: 'Privacy & Data Protection',
      icon: <SecurityIcon />,
      content: [
        {
          subtitle: 'Data Collection',
          text: 'We collect and process personal data in accordance with our Privacy Policy. By using the Service, you consent to such processing and you warrant that all data provided by you is accurate.'
        },
        {
          subtitle: 'Social Media Integration',
          text: 'When you connect your social media accounts, we may access and store certain information from those accounts as necessary to provide our services. We will only access data that you explicitly authorize.'
        },
        {
          subtitle: 'Data Security',
          text: 'We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction.'
        }
      ]
    },
    {
      id: 'payment',
      title: 'Payment Terms',
      icon: <LegalIcon />,
      content: [
        {
          subtitle: 'Subscription Plans',
          text: 'Access to premium features requires a paid subscription. Subscription fees are charged in advance on a monthly or annual basis and are non-refundable except as required by law.'
        },
        {
          subtitle: 'Auto-Renewal',
          text: 'Your subscription will automatically renew for successive periods of the same duration as the original term unless you cancel your subscription before the renewal date.'
        },
        {
          subtitle: 'Price Changes',
          text: 'We reserve the right to modify our pricing at any time. Price changes will be communicated with at least 30 days notice and will not affect your current billing cycle.'
        }
      ]
    },
    {
      id: 'intellectual',
      title: 'Intellectual Property',
      icon: <LegalIcon />,
      content: [
        {
          subtitle: 'Our Rights',
          text: 'The Service and its original content, features, and functionality are and will remain the exclusive property of GlobalCommand Center and its licensors. The Service is protected by copyright, trademark, and other laws.'
        },
        {
          subtitle: 'User Content License',
          text: 'By posting content through the Service, you grant us a worldwide, non-exclusive, royalty-free license to use, copy, reproduce, process, adapt, modify, publish, transmit, display, and distribute such content.'
        },
        {
          subtitle: 'Trademark Policy',
          text: 'All trademarks, service marks, and logos used in connection with the Service are the property of their respective owners. Use of such marks without permission is prohibited.'
        }
      ]
    },
    {
      id: 'liability',
      title: 'Limitation of Liability',
      icon: <WarningIcon />,
      content: [
        {
          subtitle: 'Service Availability',
          text: 'We strive to maintain high service availability but cannot guarantee uninterrupted access. The Service is provided "as is" without warranties of any kind, either express or implied.'
        },
        {
          subtitle: 'Limitation of Damages',
          text: 'To the fullest extent permitted by law, GlobalCommand Center shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues.'
        },
        {
          subtitle: 'Maximum Liability',
          text: 'Our total liability to you for any claims arising out of or relating to these Terms or the Service shall not exceed the amount you paid us in the twelve months preceding the claim.'
        }
      ]
    },
    {
      id: 'termination',
      title: 'Termination',
      icon: <UpdateIcon />,
      content: [
        {
          subtitle: 'Termination by You',
          text: 'You may terminate your account at any time by contacting our support team. Upon termination, your right to use the Service will cease immediately.'
        },
        {
          subtitle: 'Termination by Us',
          text: 'We may terminate or suspend your account and access to the Service immediately, without prior notice, if you breach these Terms or for any other reason at our sole discretion.'
        },
        {
          subtitle: 'Effect of Termination',
          text: 'Upon termination, all provisions of these Terms which by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, and limitations of liability.'
        }
      ]
    }
  ];

  const contactInfo = {
    company: 'Oruclicks',
    email: 'info@oruclicks.com',
    phone: '+91 9487957627',
    address: '123 Business Street, Suite 100, Tech City, TC 12345, United States'
  };

  return (
    <Box sx={{ py: 4, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            mb: 4, 
            borderRadius: 3, 
            bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LegalIcon sx={{ fontSize: 32, mr: 2 }} />
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                Terms and Conditions
              </Typography>
            </Box>
            
            <Typography variant="h6" sx={{ opacity: 0.9, mb: 3, maxWidth: '80%' }}>
              Please read these terms and conditions carefully before using our service.
            </Typography>
            
            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <UpdateIcon sx={{ mr: 1, fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                      Last Updated
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {lastUpdated}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckIcon sx={{ mr: 1, fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                      Effective Date
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {effectiveDate}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <InfoIcon sx={{ mr: 1, fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                      Version
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      2.1
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton 
                    size="small" 
                    sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }}
                    title="Print Terms"
                  >
                    <PrintIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }}
                    title="Download PDF"
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }}
                    title="Share"
                  >
                    <ShareIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            </Grid>
          </Box>
          
          {/* Background Pattern */}
          <Box 
            sx={{ 
              position: 'absolute', 
              top: 0, 
              right: 0, 
              width: '50%', 
              height: '100%', 
              opacity: 0.1,
              backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Ccircle cx="7" cy="7" r="2"/%3E%3Ccircle cx="27" cy="7" r="2"/%3E%3Ccircle cx="47" cy="7" r="2"/%3E%3Ccircle cx="7" cy="27" r="2"/%3E%3Ccircle cx="27" cy="27" r="2"/%3E%3Ccircle cx="47" cy="27" r="2"/%3E%3Ccircle cx="7" cy="47" r="2"/%3E%3Ccircle cx="27" cy="47" r="2"/%3E%3Ccircle cx="47" cy="47" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
            }} 
          />
        </Paper>

        {/* Important Notice */}
        <Alert 
          severity="info" 
          icon={<InfoIcon />}
          sx={{ 
            mb: 4, 
            borderRadius: 2,
            '& .MuiAlert-icon': { fontSize: 24 }
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Important Notice
          </Typography>
          <Typography variant="body2">
            These terms constitute a legal agreement between you and GlobalCommand Center. 
            By using our service, you acknowledge that you have read, understood, and agree to be bound by these terms.
          </Typography>
        </Alert>

        {/* Quick Navigation */}
        <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Quick Navigation
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {termsData.map((section) => (
              <Chip
                key={section.id}
                label={section.title}
                onClick={() => setExpanded(section.id)}
                variant={expanded === section.id ? 'filled' : 'outlined'}
                sx={{
                  bgcolor: expanded === section.id ? '#4B5AB6' : 'transparent',
                  color: expanded === section.id ? 'white' : '#4B5AB6',
                  '&:hover': {
                    bgcolor: expanded === section.id ? '#3a4891' : '#f1f5f9'
                  }
                }}
              />
            ))}
          </Box>
        </Paper>

        {/* Terms Content */}
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          {termsData.map((section, index) => (
            <Accordion
              key={section.id}
              expanded={expanded === section.id}
              onChange={handleAccordionChange(section.id)}
              sx={{
                '&:before': { display: 'none' },
                boxShadow: 'none',
                borderBottom: index < termsData.length - 1 ? '1px solid #e2e8f0' : 'none'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  bgcolor: expanded === section.id ? '#f8fafc' : 'white',
                  '&:hover': { bgcolor: '#f8fafc' },
                  py: 2
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ color: '#4B5AB6' }}>
                    {section.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {section.title}
                  </Typography>
                </Box>
              </AccordionSummary>
              
              <AccordionDetails sx={{ px: 4, pb: 4 }}>
                {section.content.map((item, itemIndex) => (
                  <Box key={itemIndex} sx={{ mb: itemIndex < section.content.length - 1 ? 4 : 0 }}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 600, 
                        color: '#1e293b', 
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <Box 
                        sx={{ 
                          width: 6, 
                          height: 6, 
                          borderRadius: '50%', 
                          bgcolor: '#4B5AB6' 
                        }} 
                      />
                      {item.subtitle}
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        lineHeight: 1.7, 
                        color: '#475569',
                        pl: 2
                      }}
                    >
                      {item.text}
                    </Typography>
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>

        {/* Contact Information */}
        <Card sx={{ mt: 4, borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: '#1e293b' }}>
              Contact Information
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 3, color: '#475569' }}>
              If you have any questions about these Terms and Conditions, please contact us:
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <List sx={{ py: 0 }}>
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <EmailIcon sx={{ color: '#4B5AB6' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Email"
                      secondary={
                        <Link 
                          href={`mailto:${contactInfo.email}`}
                          sx={{ color: '#4B5AB6', textDecoration: 'none' }}
                        >
                          {contactInfo.email}
                        </Link>
                      }
                    />
                  </ListItem>
                  
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <PhoneIcon sx={{ color: '#4B5AB6' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Phone"
                      secondary={contactInfo.phone}
                    />
                  </ListItem>
                </List>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <List sx={{ py: 0 }}>
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <LocationIcon sx={{ color: '#4B5AB6' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Address"
                      secondary={contactInfo.address}
                    />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Footer */}
        <Box sx={{ mt: 4, pt: 4, textAlign: 'center', borderTop: '1px solid #e2e8f0' }}>
          <Typography variant="body2" color="text.secondary">
            © 2025 GlobalCommand Center. All rights reserved. These terms and conditions are effective as of {effectiveDate}.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default TermsAndConditions;